import { EmptyState } from "@components/ui/EmptyState"
import { cn } from "@lib/cn"
import { formatDate, formatGrade } from "@lib/intl"
import { BarChart3 } from "lucide-react"
import { memo, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"

import type { ProfileEnrollment, GradeScale } from "@/types"

interface Props {
  enrollments: ProfileEnrollment[]
  scaleMax: number
  scale: GradeScale
}

// One colour per subject line. Deliberately distinct hues rather than a
// gradient — the point is telling subjects apart at a glance.
const SERIES_COLORS = [
  "rgb(79 70 229)",
  "rgb(13 148 136)",
  "rgb(217 119 6)",
  "rgb(190 24 93)",
  "rgb(2 132 199)",
]

interface Row {
  due_date: string
  [seriesKey: string]: string | number | null
}

export const ProfileGradesChart = memo(function ProfileGradesChart({
  enrollments,
  scaleMax,
  scale,
}: Props) {
  const { t } = useTranslation("student-profile")
  const [selected, setSelected] = useState<string>("all")

  const visible = useMemo(
    () =>
      selected === "all" ? enrollments : enrollments.filter((e) => e.classroom_id === selected),
    [enrollments, selected],
  )

  // One row per due date, one column per subject. Subjects are NEVER merged
  // into a single series: their tasks don't share a calendar or a difficulty,
  // so a blended line would zig-zag without meaning (PRD-40 §FR-10).
  const { rows, series } = useMemo(() => {
    const byDate = new Map<string, Row>()
    const seriesMeta: { key: string; label: string; color: string }[] = []

    visible.forEach((enrollment, index) => {
      const key = `s_${enrollment.classroom_id}`
      seriesMeta.push({
        key,
        label: enrollment.classroom_name,
        color: SERIES_COLORS[index % SERIES_COLORS.length],
      })
      enrollment.tasks.forEach((task) => {
        if (task.grade === null) return
        const row = byDate.get(task.due_date) ?? { due_date: task.due_date }
        row[key] = task.grade
        byDate.set(task.due_date, row)
      })
    })

    return {
      rows: [...byDate.values()].sort((a, b) => a.due_date.localeCompare(b.due_date)),
      series: seriesMeta,
    }
  }, [visible])

  const ariaSummary = useMemo(
    () =>
      t("chart.aria_summary", {
        subjects: visible.map((e) => e.classroom_name).join(", "),
      }),
    [t, visible],
  )

  return (
    <section className="rounded-card border-border bg-surface shadow-raised border p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-ink text-sm font-semibold">{t("chart.title")}</h2>
        {enrollments.length > 1 && (
          <div className="flex flex-wrap gap-1.5" role="group" aria-label={t("chart.filter_label")}>
            <FilterChip
              active={selected === "all"}
              onClick={() => setSelected("all")}
              label={t("chart.all_subjects")}
            />
            {enrollments.map((enrollment) => (
              <FilterChip
                key={enrollment.classroom_id}
                active={selected === enrollment.classroom_id}
                onClick={() => setSelected(enrollment.classroom_id)}
                label={enrollment.classroom_name}
              />
            ))}
          </div>
        )}
      </div>

      {rows.length === 0 ? (
        <div className="mt-3">
          <EmptyState
            icon={<BarChart3 className="h-10 w-10" />}
            title={t("chart.empty.title")}
            message={t("chart.empty.message")}
          />
        </div>
      ) : (
        <>
          <div className="mt-3 h-64 w-full" role="img" aria-label={ariaSummary}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={rows} margin={{ top: 8, right: 16, left: -8, bottom: 8 }}>
                {/* `stroke="currentColor"` + a token text colour is how a
                    recharts primitive reads the theme: the grid is a hairline in
                    both themes rather than a fixed near-white that vanishes on
                    the dark ground. */}
                <CartesianGrid vertical={false} stroke="currentColor" className="text-hairline" />
                <XAxis
                  dataKey="due_date"
                  tickFormatter={(v: string) => formatDate(v, "d MMM")}
                  interval="preserveStartEnd"
                  minTickGap={24}
                  tick={{ fontSize: 11 }}
                  className="text-ink-subtle"
                />
                <YAxis
                  domain={[0, scaleMax]}
                  ticks={[0, scaleMax / 2, scaleMax]}
                  tick={{ fontSize: 11 }}
                  width={32}
                  className="text-ink-subtle"
                />
                <Tooltip
                  contentStyle={{ fontSize: 12, borderRadius: 8, borderColor: "rgb(203 213 225)" }}
                  labelFormatter={(v: string) => formatDate(v)}
                  formatter={(value: number, key: string) => [
                    formatGrade(value, scale),
                    series.find((s) => s.key === key)?.label ?? "",
                  ]}
                />
                {series.map((s) => (
                  <Line
                    key={s.key}
                    type="monotone"
                    dataKey={s.key}
                    stroke={s.color}
                    strokeWidth={2}
                    connectNulls
                    dot={{ r: 3, strokeWidth: 0, fill: s.color }}
                    activeDot={{ r: 5 }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>

          <table className="sr-only">
            <caption>{ariaSummary}</caption>
            <thead>
              <tr>
                <th scope="col">{t("chart.date")}</th>
                {series.map((s) => (
                  <th key={s.key} scope="col">
                    {s.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.due_date}>
                  <td>{formatDate(row.due_date)}</td>
                  {series.map((s) => (
                    <td key={s.key}>
                      {typeof row[s.key] === "number"
                        ? formatGrade(row[s.key] as number, scale)
                        : "—"}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>

          <div className="text-ink-subtle mt-2 flex flex-wrap items-center gap-3 text-xs">
            {series.map((s) => (
              <span key={s.key} className="inline-flex items-center gap-1">
                <span
                  className="h-2 w-2 rounded-full"
                  style={{ backgroundColor: s.color }}
                  aria-hidden="true"
                />
                {s.label}
              </span>
            ))}
          </div>
        </>
      )}
    </section>
  )
})

function FilterChip({
  active,
  onClick,
  label,
}: {
  active: boolean
  onClick: () => void
  label: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "rounded-full px-2.5 py-1 text-xs font-medium focus:ring-2 focus:outline-none",
        active ? "bg-accent text-white" : "bg-inset text-ink-muted dark:",
      )}
    >
      {label}
    </button>
  )
}
